// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography,
} from '@mui/material'
import { format } from 'date-fns'
import { useEffect, useState } from 'react'
import { useNotify, useTranslate } from 'react-admin'

import { deadlineFetch } from '../hooks/customEndpointFetch'
import { authHeaders } from '../utils/api'
import { ALL_PERMISSIONS, PERMISSION_LABELS, type VetShare } from './types'

/**
 * Edit a live share in place (permissions / expiry / note) — no more
 * revoke+recreate. Posts whitelisted changes to
 * POST /api/vet-shares/update/:shareId (sharer-only on the backend).
 */

interface EditShareDialogProps {
  share: VetShare
  open: boolean
  onClose: () => void
  onSaved: () => void
}

// share.expiresAt is stored as LOCAL midnight (CreateShareModal does
// `new Date` → `toISOString()`), so display it in local time — slicing the
// UTC ISO string shows the previous day for any zone ahead of UTC.
const toDateInputValue = (iso?: string) => (iso ? format(new Date(iso), 'yyyy-MM-dd') : '')

// The date input gives us a bare 'yyyy-MM-dd'; parse it back as LOCAL
// midnight to match how it was stored, not UTC midnight (new Date(str)).
const parseDateInputToLocalMidnight = (value: string) => {
  const [y, m, d] = value.split('-').map(Number)
  return new Date(y, m - 1, d)
}

const EditShareDialog = ({ share, open, onClose, onSaved }: EditShareDialogProps) => {
  const translate = useTranslate()
  const notify = useNotify()

  const [permissions, setPermissions] = useState<string[]>(share.permissions)
  const [expiresAt, setExpiresAt] = useState(toDateInputValue(share.expiresAt))
  // What the date input was SEEDED with for this share — lets save() tell
  // "untouched" apart from a genuine edit, so an unrelated (e.g.
  // permissions-only) save doesn't resend a reconstructed expiresAt that
  // differs from the stored value merely because local-midnight round-trips
  // through a bare 'yyyy-MM-dd' string.
  const [initialExpiresAt, setInitialExpiresAt] = useState(toDateInputValue(share.expiresAt))
  const [note, setNote] = useState(share.note || '')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  // Re-sync when a different share is opened in the same mounted dialog
  useEffect(() => {
    const seeded = toDateInputValue(share.expiresAt)
    setPermissions(share.permissions)
    setExpiresAt(seeded)
    setInitialExpiresAt(seeded)
    setNote(share.note || '')
    setError('')
  }, [share])

  const togglePermission = (perm: string) =>
    setPermissions((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm],
    )

  const handleSave = async () => {
    setError('')
    if (permissions.length === 0) {
      setError(translate('pos.pet_shares.error_no_permissions'))
      return
    }

    const body: Record<string, unknown> = { permissions, note }
    // Only send expiresAt when the user actually touched the date field —
    // omitting it leaves the server's stored value alone; a cleared field
    // still explicitly sends null.
    if (expiresAt !== initialExpiresAt) {
      body.expiresAt = expiresAt ? parseDateInputToLocalMidnight(expiresAt).toISOString() : null
    }

    setSaving(true)
    try {
      const response = await deadlineFetch(`/api/vet-shares/update/${share.id}`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      })
      const data = await response.json()
      if (response.ok && data?.success) {
        notify('pos.pet_shares.share_updated', { type: 'success' })
        onSaved()
        onClose()
      } else {
        setError(data?.error || translate('pos.pet_shares.error_update_failed'))
      }
    } catch (_e) {
      setError(translate('pos.register_owner.error_network'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{translate('pos.pet_shares.edit_title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {share.recipient?.name || share.recipientEmail}
        </Typography>

        <Typography variant="subtitle2" sx={{ mt: 1 }}>
          {translate('pos.pet_shares.edit_permissions')}
        </Typography>
        <FormGroup>
          {ALL_PERMISSIONS.map((perm) => (
            <FormControlLabel
              key={perm}
              control={
                <Checkbox
                  checked={permissions.includes(perm)}
                  onChange={() => togglePermission(perm)}
                  disabled={saving}
                />
              }
              label={PERMISSION_LABELS[perm]}
            />
          ))}
        </FormGroup>

        <TextField
          label={translate('pos.pet_shares.edit_expiry')}
          type="date"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          fullWidth
          margin="dense"
          InputLabelProps={{ shrink: true }}
          disabled={saving}
          helperText={translate('pos.pet_shares.edit_expiry_hint')}
        />

        <TextField
          label={translate('pos.pet_shares.edit_note')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          multiline
          minRows={2}
          fullWidth
          margin="dense"
          disabled={saving}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {translate('ra.action.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {translate('pos.pet_shares.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EditShareDialog
